import { ApplicationDesktopResizer } from '@/application/viewport/ApplicationDesktopResizer'
import { ApplicationMobileResizer } from '@/application/viewport/ApplicationMobileResizer'
import Facade from '@/Facade'
import { HeavenConvertUtil } from '@/utils/HeavenConvertUtil'
import { IS_DEBUG_BUILD } from '@/utils/RuntimeEnv'
import { DeviceUtils } from '@modules/src/utils/DeviceUtils'
import { MeshH, BitmapTextH, applySpineMixin, settings, CLAMP_OPTIONS } from '@/utils/pixi-heaven-compat'
import * as PIXI from 'pixi.js'
import { ContainersNames } from '@/containers/ContainersNames'

export class ApplicationViewport {
    public static registerPixiHeaven() {
        HeavenConvertUtil.applyBitmapTextMixin(BitmapTextH.prototype)
        HeavenConvertUtil.applyMeshMixin(MeshH.prototype)
        applySpineMixin() // No-op in v8
        // settings rimossi - non più necessari in v8
    }

    public static async initializePixiViewport(): Promise<void> {
        const initialize = async () => {
            const canvas = document.querySelector<HTMLCanvasElement>('#gameCanvas')

            if (!canvas) {
                console.error('Canvas not found!')
                return
            }

            const wrapper = document.querySelector<HTMLDivElement>('#container')
            if (!wrapper) {
                console.error('Container wrapper not found!')
                return
            }

            const desktopResizer = new ApplicationDesktopResizer(canvas, wrapper)
            const mobileResizer = new ApplicationMobileResizer(canvas, wrapper)
            Facade.resizer = DeviceUtils.isMobileDevice() ? mobileResizer : desktopResizer

            let scheduledResizeFrame = 0
            let settledResizeTimeout: ReturnType<typeof setTimeout> | null = null

            const updateResizer = (force: boolean = false) => {
                if (force || Facade.resizer.isSizeChanged()) {
                    Facade.resizer = DeviceUtils.isMobileDevice() ? mobileResizer : desktopResizer
                    Facade.resizer.updateWrapperBounds()
                    Facade.resizer.updateSize(true)
                }
            }

            const scheduleViewportRefresh = () => {
                if (scheduledResizeFrame) {
                    cancelAnimationFrame(scheduledResizeFrame)
                }

                scheduledResizeFrame = requestAnimationFrame(() => {
                    scheduledResizeFrame = 0
                    updateResizer(true)
                })

                if (settledResizeTimeout) {
                    clearTimeout(settledResizeTimeout)
                }

                // Mobile browsers may report an intermediate viewport during toolbar/orientation transitions.
                settledResizeTimeout = setTimeout(() => {
                    settledResizeTimeout = null
                    updateResizer(true)
                }, 120)
            }

            // PixiJS v8: Settings configuration has changed
            // Most of these settings are now set during Application creation or are deprecated
            // Some settings moved to different locations or are no longer configurable
            if (PIXI.Mesh) {
                ;(PIXI.Mesh as any).BATCHABLE_SIZE = 1000
            }

            // PixiJS v8: skipHello() has been removed

            const application = new PIXI.Application()

            await application.init({
                canvas: canvas,
                autoStart: true,
                resolution: Facade.resizer.resolution,
                autoDensity: true,
                antialias: false,
                backgroundAlpha: 0,
                clearBeforeRender: true,
                powerPreference: 'high-performance',
                height: Facade.resizer.canvasHeight,
                width: Facade.resizer.canvasWidth,
            })

            Facade.renderer = application.renderer as PIXI.Renderer
            Facade.orientation = Facade.resizer.orientation
            Facade.app = application
            Facade.stage = application.stage

            Facade.container = new PIXI.Container()
            // PixiJS v8: Use label instead of deprecated name property
            Facade.container.label = ContainersNames.GAME_CONTAINER
            Facade.stage.addChild(Facade.container)

            // PixiJS v8: Ticker is available via application.ticker
            if (application.ticker) {
                application.ticker.add(updateResizer)
            }
            window.addEventListener('resize', scheduleViewportRefresh)
            window.addEventListener('orientationchange', scheduleViewportRefresh)
            window.visualViewport?.addEventListener('resize', scheduleViewportRefresh)
            window.visualViewport?.addEventListener('scroll', scheduleViewportRefresh)
            Facade.resizer.updateSize()

            console.log('PixiJS viewport initialized:', {
                canvas: canvas,
                wrapper: wrapper,
                width: Facade.resizer.canvasWidth,
                height: Facade.resizer.canvasHeight,
                resolution: Facade.resizer.resolution,
            })
        }

        await initialize()
    }

    /**
     * Exposing the app on window is what lets the Pixi devtools extension attach, so it is gated on
     * the build constant rather than on Facade.isDebug: that way the whole block is folded out of the
     * production bundle and there is nothing left for the extension to find.
     */
    public static registerInspector() {
        if (IS_DEBUG_BUILD) {
            const inspector = window.__PIXI_INSPECTOR_GLOBAL_HOOK__
            window.__PIXI_APP__ = Facade.app

            if (inspector != null) {
                inspector.register({ PIXI: PIXI })
            }
        }
    }

    public static syncSpineWithPixiTicker() {
        // PixiJS v8 + @esotericsoftware/spine-pixi-v8:
        // SpineBase is no longer available, the new Spine plugin handles updates differently
        // The Spine class now uses the PixiJS ticker automatically for updates
        // No manual synchronization needed
        console.log('Spine ticker sync: New Spine v8 plugin handles updates automatically')
    }
}
